import { Archive, ArchiveRestore, Pin, PinOff } from 'lucide-react';
import type { Board } from '@/lib/api/types';
import type { MenuItem } from '@/components/Menu';
import { IconButton } from '@/components/Button';
import { usePermission } from '@/features/workspace/usePermission';
import { usePinTarget } from './usePins';
import { useTidyActions } from './useTidyActions';

/** Pin/unpin + archive/unarchive for a board (S1, S7a). Icon buttons for headers; `useBoardTidyMenuItems` for menus. */
export function BoardTidyActions({ board }: { board: Board }) {
  const pin = usePinTarget('board', board.id);
  const canManage = usePermission('MANAGE_BOARD', { boardId: board.id });
  const tidy = useTidyActions();
  const archived = !!board.archived_at;
  return (
    <span className="inline-flex items-center gap-0.5" data-testid="board-tidy-actions">
      {pin.available && (
        <IconButton label={pin.pinned ? 'Unpin board' : 'Pin board'} size="sm" active={pin.pinned} onClick={pin.toggle}>
          {pin.pinned ? <PinOff size={14} /> : <Pin size={14} />}
        </IconButton>
      )}
      {canManage && (
        <IconButton label={archived ? 'Unarchive board' : 'Archive board'} size="sm" onClick={() => tidy.archiveBoard(board.id, board.name, !archived)}>
          {archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
        </IconButton>
      )}
    </span>
  );
}

export function useBoardTidyMenuItems(board: Board): MenuItem[] {
  const pin = usePinTarget('board', board.id);
  const canManage = usePermission('MANAGE_BOARD', { boardId: board.id });
  const tidy = useTidyActions();
  const archived = !!board.archived_at;
  return [
    ...(pin.available ? [{ label: pin.pinned ? 'Unpin' : 'Pin to sidebar', icon: pin.pinned ? <PinOff size={14} /> : <Pin size={14} />, onSelect: pin.toggle }] : []),
    ...(canManage ? [{ label: archived ? 'Unarchive' : 'Archive', icon: archived ? <ArchiveRestore size={14} /> : <Archive size={14} />, onSelect: () => tidy.archiveBoard(board.id, board.name, !archived) }] : []),
  ];
}
